// src/screens/PGLog.jsx
import React, { useEffect, useMemo, useState } from "react";
import "../css/PGLog.css";
import * as XLSX from "xlsx";
import useIdleLogout from "../hooks/useIdleLogout";

/**
 * Set in your .env:
 *   REACT_APP_PG_LOG_API_URL=http://192.168.131.105:3000/api/rma/logs
 */
const API_PG_LOG = (process.env.REACT_APP_PG_LOG_API_URL || "").trim();

/* ========== helpers ========== */

// Fallback: try to detect "2025-11-27 16:29:49" at line start → Date
function extractTimestamp(line) {
  if (!line) return null;
  const m = /^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2}:\d{2})/.exec(line);
  if (!m) return null;
  const d = new Date(`${m[1]}T${m[2]}`);
  const t = d.getTime();
  return Number.isFinite(t) ? t : null;
}

// Normalize backend data → rows for table
// Expected response:
// {
//   ok: true,
//   data: {
//     items: [{ id, order_no, bfs_txn_id, tag, raw_log, created_at }, ...],
//     total, page, limit, pages
//   }
// }
function normalizeRows(raw) {
  if (!Array.isArray(raw)) return [];

  return raw.map((item, idx) => {
    if (typeof item === "string") {
      return {
        id: idx + 1,
        orderNo: "",
        bfsTxnId: "",
        tag: "",
        createdAt: "",
        response: item,
      };
    }

    const orderNo =
      item.order_no ||
      item.orderNo ||
      item.bfs_orderNo ||
      item.bfs_order_no ||
      "";

    const bfsTxnId =
      item.bfs_txn_id ||
      item.bfsTxnId ||
      item.bfs_txnId ||
      item.bfsTxnID ||
      "";

    const tag =
      item.tag ||
      item.msgType ||
      item.bfs_msgType ||
      item.type ||
      item.code ||
      "";

    const createdAt =
      item.created_at ||
      item.createdAt ||
      item.timestamp ||
      item.time ||
      item.loggedAt ||
      "";

    const responseText =
      item.raw_log ||
      item.response ||
      item.message ||
      item.log ||
      item.payload ||
      item.raw ||
      item.data ||
      "";

    const response =
      typeof responseText === "string"
        ? responseText
        : JSON.stringify(responseText, null, 2);

    return {
      id: item.id ?? idx + 1,
      orderNo: String(orderNo || ""),
      bfsTxnId: String(bfsTxnId || ""),
      tag: String(tag || ""),
      createdAt: createdAt ? String(createdAt) : "",
      response: String(response ?? ""),
    };
  });
}

/* ========== component ========== */

export default function PGLog() {
  useIdleLogout(); 
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ---- load all logs once ----
  const fetchLogs = async () => {
    if (!API_PG_LOG) {
      setRows([]);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const res = await fetch(API_PG_LOG);
      if (!res.ok) throw new Error(`Failed to fetch PG logs (${res.status})`);
      const json = await res.json();

      const raw =
        (Array.isArray(json?.data?.items) && json.data.items) ||
        (Array.isArray(json?.data) && json.data) ||
        (Array.isArray(json?.logs) && json.logs) ||
        (Array.isArray(json) && json) ||
        [];

      const norm = normalizeRows(raw);
      setRows(norm);
    } catch (err) {
      console.error("[PGLog] fetch error:", err);
      setError(err?.message || String(err));
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  // ---- filtered rows (client-side search + date range) ----
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const fromTs = fromDate ? new Date(fromDate).setHours(0, 0, 0, 0) : null;
    const toTs = toDate ? new Date(toDate).setHours(23, 59, 59, 999) : null;

    return rows.filter((r) => {
      // 🔍 search across ALL column values:
      // ORDER NO, BFS TXN ID, TAG, CREATED AT, RAW LOG
      const combined = `${r.orderNo} ${r.bfsTxnId} ${r.tag} ${
        r.createdAt
      } ${r.response}`.toLowerCase();

      if (q && !combined.includes(q)) return false;

      if (fromTs || toTs) {
        let ts = null;

        if (r.createdAt) {
          const d = new Date(r.createdAt);
          const t = d.getTime();
          if (Number.isFinite(t)) ts = t;
        }
        if (!ts) {
          ts = extractTimestamp(r.response);
        }

        if (!ts) return false;
        if (fromTs && ts < fromTs) return false;
        if (toTs && ts > toTs) return false;
      }

      return true;
    });
  }, [rows, search, fromDate, toDate]);

  // ---- Excel export ----
  const handleExport = () => {
    if (!filtered.length) return;

    const now = new Date();
    const exportedAt = now.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });

    const rangeLabel =
      fromDate || toDate
        ? `Exported: ${exportedAt}  (range: ${fromDate || "—"} to ${
            toDate || "—"
          })`
        : `Exported: ${exportedAt}`;

    const data = [
      ["RMA Response Logs"],
      [rangeLabel],
      [],
      ["SI.NO", "ORDER NO", "BFS TXN ID", "TAG", "CREATED AT", "RAW LOG"],
      ...filtered.map((r, idx) => [
        idx + 1,
        r.orderNo,
        r.bfsTxnId,
        r.tag,
        r.createdAt,
        r.response,
      ]),
    ];

    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "PG Logs");
    XLSX.writeFile(wb, "pg-rma-response-logs.xlsx");
  };

  return (
    <div className="pg-log-page">
      <div className="pg-log-card">
        <div className="pg-log-header">
          <h3 className="pg-log-title">RMA Response Logs</h3>
        </div>

        {/* ---- toolbar ---- */}
        <div className="pg-log-toolbar">
          {/* LEFT: free text search – matches order no, tag, etc. */}
          <div className="pg-log-toolbar-left">
            <div className="pg-log-search">
              <span
                className="pg-log-search-icon bi bi-search"
                aria-hidden="true"
              />
              <input
                type="text"
                className="pg-log-input pg-log-input-search"
                placeholder="Search order, tag, txn id, response..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {/* RIGHT: date range + export (no pills) */}
          <div className="pg-log-toolbar-right">
            <div className="pg-log-date-pill">
              <input
                type="date"
                className="pg-log-input pg-log-input-date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <span className="pg-log-to-label">to</span>
            <div className="pg-log-date-pill">
              <input
                type="date"
                className="pg-log-input pg-log-input-date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>

            <button
              type="button"
              className="pg-log-export-btn"
              onClick={handleExport}
              disabled={!filtered.length}
            >
              Export Excel
            </button>
          </div>
        </div>

        {error && (
          <div className="pg-log-error alert alert-danger mb-2">{error}</div>
        )}

        {/* ---- table ---- */}
        <div className="pg-log-table-wrapper">
          <table className="pg-log-table">
            <thead>
              <tr>
                <th className="pg-log-col-si">SI.NO</th>
                <th className="pg-log-col-order">ORDER NO</th>
                <th className="pg-log-col-bfs">BFS TXN ID</th>
                <th className="pg-log-col-tag">TAG</th>
                <th className="pg-log-col-date">CREATED AT</th>
                <th>RAW LOG</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="pg-log-loading">
                    Loading logs…
                  </td>
                </tr>
              )}

              {!loading && !filtered.length && (
                <tr>
                  <td colSpan={6} className="pg-log-empty">
                    No logs found for the current filters.
                  </td>
                </tr>
              )}

              {!loading &&
                filtered
                  .slice(0, 5) // 🔥 show only first 5 rows in the table
                  .map((row, idx) => (
                    <tr key={row.id ?? idx}>
                      <td>{idx + 1}</td>
                      <td>{row.orderNo || "—"}</td>
                      <td>{row.bfsTxnId || "—"}</td>
                      <td>{row.tag || "—"}</td>
                      <td>
                        <small className="text-muted">
                          {row.createdAt
                            ? new Date(row.createdAt).toLocaleString()
                            : "—"}
                        </small>
                      </td>
                      <td>
                        <pre className="pg-log-response">{row.response}</pre>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      </div>

      <footer className="dashboard-footer">
        <small>
          © {new Date().getFullYear()}{" "}
          <strong>སྟབས་བདེ།</strong> Admin • All rights reserved
        </small>
      </footer>
    </div>
  );
}
